/**
 * Strips invisible Unicode steganography characters injected by the Contentful
 * Live Preview SDK (`encodeCPAResponse`). Call this before using any Contentful
 * text field value as a CSS property or in a string comparison.
 */
export function stripEncoding(value: string): string {
  // Removes zero-width and other invisible format characters used by steganography.
  return value.replace(/[\u200b-\u200f\u2028-\u202f\u2060-\u206f\ufeff\u00ad]/g, '');
}

/** Returns { className, style } for a background value.
 *  Accepts:
 *  - Hex colors:  #D8F6F0
 *  - CSS gradients: linear-gradient(137deg, #9ED7FF 0%, #E9F5FE 100%)
 *  - Legacy keyword values: white, light-gray, dark, primary, secondary
 */
export function bgProps(color?: string): {
  className: string;
  style?: React.CSSProperties;
} {
  if (!color) return { className: 'bg-white' };
  color = stripEncoding(color);

  if (color.startsWith('#') || color.startsWith('rgb')) {
    return { className: '', style: { backgroundColor: color } };
  }

  if (color.includes('gradient') || color.includes('linear-') || color.includes('radial-')) {
    return { className: '', style: { background: color.replace(/;\s*$/, '') } };
  }

  const legacyMap: Record<string, string> = {
    white: 'bg-white',
    'light-gray': 'bg-gray-50',
    dark: 'bg-gray-900',
    primary: 'bg-primary',
    secondary: 'bg-secondary',
  };

  return { className: legacyMap[color] ?? 'bg-white' };
}

/** Convenience — returns only the className (for components not yet using inline styles). */
export function bgClass(color?: string): string {
  return bgProps(color).className;
}
